import { useState, useMemo, useEffect } from 'react';
import { useAuth, type UserRole } from '@services/auth';
import { useI18n } from '../i18n';

type FAQItem = {
	question: string;
	answer: string;
};

const FAQ_BY_ROLE: Record<UserRole, FAQItem[]> = {
	seeker: [
		{
			question: 'How do I create a garden plan?',
			answer:
				'Go to "Create Plan" in the sidebar and complete the garden questionnaire. You get a tailored plan built from what you tell us about your garden.'
		},
		{
			question: 'How do I find gardeners?',
			answer:
				'After you publish a plan, it appears in the Marketplace where gardeners browse work. They submit offers you can compare and accept.'
		},
		{
			question: 'What is the calendar for?',
			answer:
				'The sidebar calendar highlights days with tasks, milestones, or visits from your projects. Click a date to see what is scheduled.'
		},
		{
			question: 'How do I track my projects?',
			answer:
				'Open "My Projects" for active jobs after you accept an offer. You can follow timelines, tasks, and progress from there.'
		},
		{
			question: 'Can I edit my plan after creation?',
			answer:
				'Yes. Open "My Plans", choose a plan, and use the questionnaire or plan editor. Save when you are done so updates stay in sync.'
		}
	],
	gardener: [
		{
			question: 'How do I find new work?',
			answer:
				'Use "Marketplace" to browse published plans. Open one to read the brief, then prepare and send an offer if it fits your services.'
		},
		{
			question: 'What happens after I send an offer?',
			answer:
				'The homeowner reviews offers in their flow. Check "Projects" (including sent offers) to see whether yours was accepted, declined, or is still pending.'
		},
		{
			question: 'How do schedule and tasks work?',
			answer:
				'"Schedule" and "Tasks" reflect timelines from your projects. The mini calendar in the sidebar gives a quick view of upcoming work.'
		},
		{
			question: 'Marketplace vs Projects?',
			answer:
				'Marketplace is for discovering new leads. Projects is for jobs you are already involved in—including offers you have submitted and active work.'
		},
		{
			question: 'How do I keep the client updated?',
			answer:
				'Use the project detail view: timelines, tasks, and milestones help the homeowner see progress without extra back-and-forth. For anything that needs a quick note or a proper conversation, open the Messages tab on that project and write to them there.'
		}
	],
	company: [
		{
			question: 'How does our team find client plans?',
			answer:
				'Use "Marketplace" like your gardeners: published homeowner plans appear there so you can review briefs and submit company offers.'
		},
		{
			question: 'How do I coordinate crews and jobs?',
			answer:
				'"Projects" and "Schedule" show who is on which job and when. "Team" is for attendance and check-ins across your organization.'
		},
		{
			question: 'What is Tasks used for?',
			answer:
				'Operational to-dos tied to projects—so field and office work stays visible and nothing is missed between visits.'
		},
		{
			question: 'Can we create a plan for a client?',
			answer:
				'Yes. Under Garden Seekers, "Create Plan" supports a company-direct flow when you are building or refining a plan with a client.'
		},
		{
			question: 'What does the Dashboard show?',
			answer:
				'A high-level snapshot of activity: marketplace, projects, and team metrics where your setup exposes them.'
		}
	],
	admin: [
		{
			question: 'Where are platform metrics?',
			answer:
				'Open "Admin Insights" in the Admin section for analytics and overview data about how the product is used.'
		},
		{
			question: 'How do I handle moderation?',
			answer:
				'Use "Moderation" for review workflows. Pair it with "Issues" when users flag specific problems (for example garden detection reports).'
		},
		{
			question: 'What is the Issues page?',
			answer:
				'A queue for user-submitted reports and flags so you can triage, investigate, and resolve them in one place.'
		},
		{
			question: 'Why do I see seeker and gardener menus?',
			answer:
				'Admins can access the same areas as other roles to reproduce problems, guide users, and verify behavior end-to-end.'
		},
		{
			question: 'Quick tips for supporting users?',
			answer:
				'Walk through their role\'s sidebar: seekers live in My Plans / My Projects; gardeners in Marketplace / Projects / Tasks. Match the FAQ to their role when helping.'
		}
	]
};

const FAQ_BY_ROLE_DE: Record<UserRole, FAQItem[]> = {
	seeker: [
		{ question: 'Wie erstelle ich einen Gartenplan?', answer: 'Gehen Sie in der Seitenleiste auf "Plan erstellen" und füllen Sie den Garten-Fragebogen aus. Danach erhalten Sie einen auf Ihren Garten zugeschnittenen Plan.' },
		{ question: 'Wie finde ich Gärtner?', answer: 'Nach der Veröffentlichung erscheint Ihr Plan im Marktplatz. Dort senden Gärtner Angebote, die Sie vergleichen und annehmen können.' },
		{ question: 'Wofür ist der Kalender?', answer: 'Der Kalender in der Seitenleiste markiert Tage mit Aufgaben, Meilensteinen oder Besuchen. Klicken Sie auf ein Datum, um den Zeitplan zu sehen.' },
		{ question: 'Wie verfolge ich meine Projekte?', answer: 'Öffnen Sie "Meine Projekte", sobald Sie ein Angebot angenommen haben. Dort sehen Sie Zeitpläne, Aufgaben und Fortschritt.' },
		{ question: 'Kann ich meinen Plan nach der Erstellung bearbeiten?', answer: 'Ja. Öffnen Sie "Meine Pläne", wählen Sie einen Plan und bearbeiten Sie ihn im Fragebogen oder Plan-Editor. Speichern Sie danach die Änderungen.' }
	],
	gardener: [
		{ question: 'Wie finde ich neue Aufträge?', answer: 'Nutzen Sie den "Marktplatz", um veröffentlichte Pläne zu durchsuchen. Öffnen Sie einen Plan und senden Sie ein Angebot, wenn er zu Ihren Leistungen passt.' },
		{ question: 'Was passiert nach dem Senden eines Angebots?', answer: 'Der Hausbesitzer prüft Angebote in seinem Ablauf. Unter "Projekte" (inkl. gesendeter Angebote) sehen Sie, ob Ihr Angebot angenommen, abgelehnt oder noch offen ist.' },
		{ question: 'Wie funktionieren Zeitplan und Aufgaben?', answer: '"Zeitplan" und "Aufgaben" spiegeln die Abläufe Ihrer Projekte wider. Der Mini-Kalender in der Seitenleiste zeigt kommende Arbeit auf einen Blick.' },
		{ question: 'Marktplatz oder Projekte?', answer: 'Im Marktplatz finden Sie neue Anfragen. "Projekte" ist für laufende Aufträge und bereits gesendete Angebote.' },
		{ question: 'Wie halte ich den Kunden auf dem Laufenden?', answer: 'Nutzen Sie die Projektansicht mit Zeitplan, Aufgaben und Meilensteinen. Für direkte Kommunikation verwenden Sie die Nachrichten im jeweiligen Projekt.' }
	],
	company: [
		{ question: 'Wie findet unser Team Kundenpläne?', answer: 'Nutzen Sie den "Marktplatz" wie Ihre Gärtner: veröffentlichte Pläne erscheinen dort, damit Sie Anforderungen prüfen und Angebote senden können.' },
		{ question: 'Wie koordiniere ich Teams und Aufträge?', answer: '"Projekte" und "Zeitplan" zeigen, wer woran arbeitet und wann. "Team" dient für Anwesenheit und Check-ins.' },
		{ question: 'Wofür ist "Aufgaben"?', answer: 'Für operative Aufgaben, die Projekten zugeordnet sind – damit Büro- und Feldarbeit sichtbar bleibt und nichts verloren geht.' },
		{ question: 'Können wir einen Plan für einen Kunden erstellen?', answer: 'Ja. Unter "Gartensuchende" unterstützt "Plan erstellen" auch einen direkten Firmenablauf für Kunden.' },
		{ question: 'Was zeigt das Dashboard?', answer: 'Eine Übersicht über Marktplatz, Projekte und Team-Kennzahlen – je nach Ihrer aktuellen Konfiguration.' }
	],
	admin: [
		{ question: 'Wo finde ich Plattformmetriken?', answer: 'Öffnen Sie "Admin Insights" im Admin-Bereich für Analysen und Nutzungsübersichten.' },
		{ question: 'Wie bearbeite ich Moderation?', answer: 'Verwenden Sie "Moderation" für Review-Workflows und "Issues" für gemeldete Probleme.' },
		{ question: 'Was ist die Issues-Seite?', answer: 'Eine zentrale Liste für gemeldete Probleme und Flags, damit Sie triagieren, prüfen und lösen können.' },
		{ question: 'Warum sehe ich Menüs für mehrere Rollen?', answer: 'Admins können Bereiche anderer Rollen öffnen, um Probleme nachzustellen und End-to-End zu prüfen.' },
		{ question: 'Schnelle Tipps für den Support?', answer: 'Orientieren Sie sich an der Seitenleiste der jeweiligen Rolle und nutzen Sie passende FAQ-Antworten je nach Nutzerrolle.' }
	]
};

const FAQ_BY_ROLE_HR: Record<UserRole, FAQItem[]> = {
	seeker: [
		{ question: 'Kako mogu kreirati vrtni plan?', answer: 'U bočnoj traci otvorite "Kreiraj plan" i ispunite vrtni upitnik. Dobit ćete plan prilagođen vašem vrtu.' },
		{ question: 'Kako pronaći vrtlare?', answer: 'Nakon objave, plan se pojavljuje na Marketplaceu gdje vrtlari šalju ponude koje možete usporediti i prihvatiti.' },
		{ question: 'Čemu služi kalendar?', answer: 'Kalendar u bočnoj traci označava dane sa zadacima, prekretnicama ili posjetama. Kliknite datum za detalje.' },
		{ question: 'Kako pratim svoje projekte?', answer: 'Otvorite "Moji projekti" nakon prihvaćanja ponude. Tamo pratite vremenski plan, zadatke i napredak.' },
		{ question: 'Mogu li uređivati plan nakon izrade?', answer: 'Da. U "Moji planovi" otvorite plan i uredite ga kroz upitnik ili editor plana, zatim spremite izmjene.' }
	],
	gardener: [
		{ question: 'Kako pronaći nove poslove?', answer: 'Koristite "Marketplace" za pregled objavljenih planova. Otvorite plan i pošaljite ponudu ako odgovara vašim uslugama.' },
		{ question: 'Što se događa nakon slanja ponude?', answer: 'Vlasnik pregledava ponude. U "Projektima" vidite je li ponuda prihvaćena, odbijena ili još na čekanju.' },
		{ question: 'Kako rade raspored i zadaci?', answer: '"Raspored" i "Zadaci" prate tijek vaših projekata. Mini kalendar daje brz pregled nadolazećih aktivnosti.' },
		{ question: 'Marketplace ili Projekti?', answer: 'Marketplace je za nove prilike. "Projekti" su za poslove na kojima već radite i poslane ponude.' },
		{ question: 'Kako klijenta držati ažurnim?', answer: 'Koristite prikaz projekta s vremenskim planom, zadacima i prekretnicama. Za komunikaciju koristite poruke unutar projekta.' }
	],
	company: [
		{ question: 'Kako naš tim pronalazi planove klijenata?', answer: 'Koristite "Marketplace" kao i vrtlari: ondje vidite objavljene planove i možete slati ponude.' },
		{ question: 'Kako koordiniram timove i poslove?', answer: '"Projekti" i "Raspored" pokazuju tko radi na kojem poslu i kada. "Tim" služi za prisutnost i prijave.' },
		{ question: 'Za što služe "Zadaci"?', answer: 'Za operativne zadatke vezane uz projekte kako bi terenski i uredski rad ostali usklađeni.' },
		{ question: 'Možemo li izraditi plan za klijenta?', answer: 'Da. Pod "Vlasnici vrta", opcija "Kreiraj plan" podržava i izravan tijek za tvrtku.' },
		{ question: 'Što prikazuje nadzorna ploča?', answer: 'Sažetak aktivnosti: marketplace, projekti i timske metrike prema vašoj konfiguraciji.' }
	],
	admin: [
		{ question: 'Gdje su metrike platforme?', answer: 'Otvorite "Admin Insights" u administratorskom dijelu za analitiku i pregled korištenja.' },
		{ question: 'Kako upravljam moderacijom?', answer: 'Koristite "Moderation" za pregled i "Issues" za prijavljene probleme korisnika.' },
		{ question: 'Što je stranica Issues?', answer: 'Centralni pregled prijava i oznaka korisnika za trijažu, istragu i rješavanje.' },
		{ question: 'Zašto vidim izbornike više uloga?', answer: 'Administratori mogu otvoriti područja drugih uloga radi reprodukcije problema i provjere toka.' },
		{ question: 'Brzi savjeti za podršku korisnicima?', answer: 'Pratite izbornik njihove uloge i koristite FAQ odgovore koji odgovaraju toj ulozi.' }
	]
};

export default function SidebarFAQ() {
	const { user } = useAuth();
	const { language } = useI18n();
	const [isOpen, setIsOpen] = useState(false);
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

	const items = useMemo(() => {
		const role = user?.role ?? 'seeker';
		if (language === 'de') return FAQ_BY_ROLE_DE[role] ?? FAQ_BY_ROLE_DE.seeker;
		if (language === 'hr') return FAQ_BY_ROLE_HR[role] ?? FAQ_BY_ROLE_HR.seeker;
		return FAQ_BY_ROLE[role] ?? FAQ_BY_ROLE.seeker;
	}, [user?.role, language]);

	useEffect(() => {
		setExpandedIndex(null);
	}, [user?.role]);

	const toggleItem = (index: number) => {
		setExpandedIndex(expandedIndex === index ? null : index);
	};

	return (
		<div className="sidebar-faq">
			<button
				type="button"
				className="sidebar-faq-toggle"
				onClick={() => setIsOpen(!isOpen)}
				aria-expanded={isOpen}
			>
				<i className="bi bi-question-circle me-2"></i>
				<span className="sidebar-faq-title">FAQ</span>
				<i className={`bi bi-chevron-${isOpen ? 'up' : 'down'} ms-auto`}></i>
			</button>

			{isOpen && (
				<div className="sidebar-faq-content">
					{items.map((item, index) => (
						<div key={`${user?.role ?? 'seeker'}-${index}`} className="sidebar-faq-item">
							<button
								type="button"
								className="sidebar-faq-question"
								onClick={() => toggleItem(index)}
								aria-expanded={expandedIndex === index}
							>
								<span>{item.question}</span>
								<i className={`bi bi-chevron-${expandedIndex === index ? 'up' : 'down'}`}></i>
							</button>
							{expandedIndex === index && (
								<div className="sidebar-faq-answer">{item.answer}</div>
							)}
						</div>
					))}
				</div>
			)}
		</div>
	);
}
